
import Vue, { PropOptions } from 'vue';
import Ad, { AdClickPeriod } from '@/store/modules/ads/ad';
import { AdClickUser, clickUsers } from '@/api/endpoints/ads';
import { getCancelTokenSource, isCancel } from '@/api/utils';
import { formatDate, formatNumber, i18next } from '@/i18n';

export default Vue.extend({
  name: 'AdClickUsersModal',
  props: {
    ad: { type: Ad, required: true },
    period: { type: Object, default: null } as PropOptions<AdClickPeriod | null>,
    trackingMessage: { type: String, default: '' },
  },
  data() {
    return {
      users: [] as AdClickUser[],
      currentPage: 1,
      lastPage: 1,
      isLoading: false,
      hasError: false,
      cancelSource: getCancelTokenSource(),
      returnFocus: null as HTMLElement | null,
    };
  },
  methods: {
    formatNumber,
    formatClickDate(value: string): string {
      if (!this.period) return formatDate(value);
      return new Date(value).toLocaleDateString(i18next.language, {
        month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
      });
    },
    formatClickTime(value: string): string {
      return new Date(value).toLocaleTimeString(i18next.language, {
        hour: 'numeric', minute: '2-digit', ...(this.period ? { timeZone: 'UTC' } : {}),
      });
    },
    async loadPage(page: number) {
      this.isLoading = true;
      this.hasError = false;
      this.currentPage = page;
      try {
        const { data } = await clickUsers(this.ad.id, page, { cancelToken: this.cancelSource.token }, this.period);
        this.users = data.data;
        this.currentPage = data.current_page;
        this.lastPage = data.last_page;
      } catch (error) {
        if (!isCancel(error)) this.hasError = true;
      } finally {
        this.isLoading = false;
      }
    },
    keepFocusInside(event: KeyboardEvent) {
      const controls = (this.$el as HTMLElement).querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex="0"]');
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
  },
  created() {
    this.loadPage(1);
  },
  mounted() {
    this.returnFocus = document.activeElement as HTMLElement;
    // The modal plugin makes its wrapper visible after mounting this component.
    window.requestAnimationFrame(() => (this.$refs.closeButton as HTMLElement | undefined)?.focus());
  },
  beforeDestroy() {
    this.cancelSource.cancel();
    this.returnFocus?.focus();
  },
});
